import { JSONContent } from "@tiptap/core";
import { normalizeKey } from "./reportInfoFieldAliases";

const nodeText = (node: JSONContent): string =>
  node.text ?? (node.content || []).map(nodeText).join(
    node.type === "paragraph" || node.type === "heading" ? "" : " ",
  );

const isColumnHeader = (row: JSONContent): boolean => {
  const labels = (row.content || []).map((cell) => normalizeKey(nodeText(cell)));
  return labels.includes("serialnumber") && labels.includes("pmax");
};

const isSectionTitle = (row: JSONContent): boolean =>
  row.content?.length === 1 && (row.content[0].attrs?.colspan || 1) > 1;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/**
 * Keeps measurement and percent-deviation sections in one STC table distinct.
 * Only returned modules get data rows; absent control data is not fabricated.
 */
export const bindPerformanceSections = (
  rows: JSONContent[],
  records: Record<string, unknown>[],
): JSONContent[] | null => {
  if (
    rows.filter(isColumnHeader).length < 2 ||
    !rows.some((row) => nodeText(row).toLowerCase().includes("deviation"))
  ) return null;

  const result: JSONContent[] = [];
  let section = "measurement_result";
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    if (isSectionTitle(row)) {
      section = nodeText(row).toLowerCase().includes("deviation")
        ? "deviation"
        : "measurement_result";
    }
    result.push(row);
    if (!isColumnHeader(row)) continue;

    let end = index + 1;
    while (
      end < rows.length &&
      !isSectionTitle(rows[end]) &&
      !isColumnHeader(rows[end])
    ) end++;
    const template = rows[index + 1];
    const headerCells = row.content || [];
    const sectionName = section;
    records.forEach((record) => {
      const data = record[sectionName];
      if (!isRecord(data)) {
        throw new Error(`Missing ${sectionName} data for serial number ${record.serial_number}.`);
      }
      if (record.serial_number == null || record.serial_number === "") {
        throw new Error("A performance data record has no serial number.");
      }
      result.push({
        type: "tableRow",
        attrs: template && index + 1 < end ? template.attrs : undefined,
        content: headerCells.map((header, column) => {
          const label = normalizeKey(nodeText(header));
          const key = Object.keys(data).find((name) => normalizeKey(name) === label);
          const value = label === "serialnumber"
            ? record.serial_number
            : key === undefined ? undefined : data[key];
          if (value !== null && value !== undefined && typeof value === "object") {
            throw new Error(`Invalid performance value for "${nodeText(header)}".`);
          }
          const text = value == null || value === ""
            ? "N/A"
            : typeof value === "number" && !Number.isInteger(value)
              ? value.toFixed(2)
              : String(value);
          const source = index + 1 < end ? template?.content?.[column] : undefined;
          return {
            type: "tableCell",
            attrs: source?.attrs || { ...header.attrs, rowspan: 1 },
            content: [{
              type: "paragraph",
              attrs: source?.content?.[0]?.attrs,
              content: [{ type: "text", text, marks: source?.content?.[0]?.content?.[0]?.marks }],
            }],
          };
        }),
      });
    });
    index = end - 1;
  }
  return result;
};
